import { useEffect, useMemo, useRef, useState } from "react"
import {
  ArrowLeftRight,
  Beer,
  Check,
  ChevronDown,
  Cylinder,
  Refrigerator,
  ScanLine,
  X,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Html5Qrcode } from "html5-qrcode"
import { useTranslation } from "react-i18next"
import { T } from "../theme"
import { BottomSheet, MField, MStageBadge, MTagChip, Touchable } from "../primitives"
import { assetDetailFields, assetSummarySegments, csrfToken } from "../utils"
import { MoveStageForm } from "./MoveStageForm"
import type { MoveStageFormValues } from "./MoveStageForm"
import type {
  AssetTypeKey,
  Customer,
  MobileAsset,
  Product,
  StagesByType,
  StageWithRules,
  Tag,
} from "../types"

interface ScanScreenProps {
  /** Per-type stage map — drives which stages and rules each group's form sees. */
  stagesByType: StagesByType
  customers: Customer[]
  products: Product[]
  tags: Tag[]
  onClose: () => void
  /**
   * Submit the bulk stage move for one asset-type group. The scan screen calls
   * this once per type when the batch is mixed. Failure errors flow back via
   * the returned promise so the form can render them inline.
   */
  onConfirm: (input: {
    asset_type: AssetTypeKey
    asset_ids: Array<MobileAsset["id"]>
    values: MoveStageFormValues
  }) => Promise<{ ok: true } | { ok: false; errors: Record<string, string> }>
}

interface ResolveResponse {
  asset?: MobileAsset
  error?: string
}

async function resolveCode(code: string): Promise<MobileAsset | null> {
  try {
    const res = await fetch("/scan/resolve", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ code }),
    })
    if (!res.ok) return null
    const data = (await res.json()) as ResolveResponse
    return data.asset || null
  } catch {
    return null
  }
}

export function ScanScreen({
  stagesByType,
  customers,
  products,
  tags,
  onClose,
  onConfirm,
}: ScanScreenProps) {
  const { t } = useTranslation()

  // The installed PWA keeps a safe-area gap below the layout viewport (we
  // deliberately don't use viewport-fit=cover) and that gap shows the document
  // background. Darken it while the camera UI is open so it doesn't read as a
  // white strip under the scanner; restore on close.
  useEffect(() => {
    const el = document.documentElement
    const prev = el.style.background
    el.style.background = T.ink
    return () => {
      el.style.background = prev
    }
  }, [])

  const [scanned, setScanned] = useState<MobileAsset[]>([])
  const [flash, setFlash] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [scanError, setScanError] = useState<string | null>(null)
  const [moveSheetOpen, setMoveSheetOpen] = useState(false)
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false)
  // Which review-sheet rows show their detail panel. A Set rather than a
  // single id: comparing two assets side by side is the point of the sheet,
  // so opening one must not collapse another.
  const [expandedIds, setExpandedIds] = useState<Set<MobileAsset["id"]>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  // Errors are scoped per asset-type group — the bulk endpoint runs once per
  // type so each group's form can render its own validation errors.
  const [submitErrors, setSubmitErrors] = useState<Partial<Record<AssetTypeKey, Record<string, string>>>>({})
  const [confirmation, setConfirmation] = useState<{
    count: number
    targetStages: StageWithRules[]
  } | null>(null)

  // Group scanned assets by type so a mixed batch (e.g. kegs + cylinders)
  // shows one move-form card per type, each with its own stages and rules.
  const grouped = useMemo(() => {
    const groups: Partial<Record<AssetTypeKey, MobileAsset[]>> = {}
    for (const a of scanned) {
      const type = a.assetable_type as AssetTypeKey
      if (!groups[type]) groups[type] = []
      groups[type]!.push(a)
    }
    return groups
  }, [scanned])

  const groupTypes = useMemo(
    () => Object.keys(grouped) as AssetTypeKey[],
    [grouped],
  )

  // The html5-qrcode library mounts its <video> into a div looked up by id.
  // We attach the id via a ref instead of useId() so the same DOM node is
  // visible to the library throughout React's render cycle.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const containerId = "mobile-qr-scanner"
  const scannerRef = useRef<Html5Qrcode | null>(null)
  // Promise we await before pausing/resuming. Set inside the mount effect once
  // `scanner.start()` has resolved; everything else waits on it so we never
  // touch the scanner mid-bringup. Replaced by `restartScanner` when we have
  // to rebuild the scanner after a wedge.
  const readyRef = useRef<Promise<void>>(Promise.resolve())
  // Codes currently in flight or just resolved — debounces noisy multi-frame reads.
  const inFlightRef = useRef<Set<string>>(new Set())
  // Mirror of `scanned` for use inside the scanner callback (which captures stale state).
  const scannedIdsRef = useRef<Set<MobileAsset["id"]>>(new Set())
  // Pause tracker so the open bottom-sheet doesn't keep reading codes behind it.
  const pausedRef = useRef(false)
  // Library state constants. Html5Qrcode exposes getState() but not the enum
  // (it lives in a separate ESM file); hardcoding the numbers is what every
  // sample in the repo does.
  const SCANNER_STATE_SCANNING = 2
  const SCANNER_STATE_PAUSED = 3

  useEffect(() => {
    scannedIdsRef.current = new Set(scanned.map((a) => a.id))
  }, [scanned])

  useEffect(() => {
    const nextPaused = moveSheetOpen || reviewSheetOpen || confirmation !== null
    pausedRef.current = nextPaused
    // Mirror the high-level paused state onto the library. Without this, a
    // sheet closed during the post-decode cooldown (where we paused the
    // library and were waiting to resume) would leave the camera frozen.
    // State-checked so a redundant call (we're already in the target state)
    // is a no-op instead of an exception that throws the state machine off.
    const scanner = scannerRef.current
    if (!scanner) return
    try {
      const state = scanner.getState()
      if (nextPaused && state === SCANNER_STATE_SCANNING) {
        scanner.pause(false)
      } else if (!nextPaused && state === SCANNER_STATE_PAUSED) {
        scanner.resume()
      }
    } catch {
      // ignore — scanner mid-transition
    }
  }, [moveSheetOpen, reviewSheetOpen, confirmation])

  // Latest decoder, held in a ref so the camera mount effect can stay free of
  // dependencies. Without this, any change to `onDecoded` (e.g. if `t` from
  // useTranslation gets a new reference) would tear down and re-mount the
  // scanner mid-session — which is what was breaking the second scan.
  const onDecodedRef = useRef<(code: string) => void>(() => {})

  useEffect(() => {
    // State-checked pause: a no-op unless the scanner is actually SCANNING.
    // Without the guard the library throws (already paused, mid-transition,
    // not started yet) and the catch swallowed it — but the scanner could
    // already be in PAUSED, so the matching resume() ended up double-firing
    // and leaving the library wedged.
    const safePause = () => {
      const scanner = scannerRef.current
      if (!scanner) return
      try {
        if (scanner.getState() === SCANNER_STATE_SCANNING) {
          scanner.pause(/* shouldPauseVideo */ false)
        }
      } catch {
        // ignore
      }
    }

    // State-checked resume + watchdog. If we expected to resume but the
    // scanner didn't come back to SCANNING (resume threw, or iOS quietly
    // paused the <video> and renderedCamera.resume's callback never fired),
    // rebuild the scanner from scratch via restartScannerRef. This is the
    // recovery path for the "scan process gets stuck" complaint.
    const safeResume = async () => {
      const scanner = scannerRef.current
      if (!scanner) return
      try {
        if (scanner.getState() === SCANNER_STATE_PAUSED) {
          scanner.resume()
        }
      } catch {
        // fall through to the wedge check below
      }
      if (scanner.getState() !== SCANNER_STATE_SCANNING) {
        await restartScannerRef.current()
      }
    }

    onDecodedRef.current = async (code: string) => {
      if (pausedRef.current) return
      if (inFlightRef.current.has(code)) return
      inFlightRef.current.add(code)
      // Pause the library's forever-scan loop while we resolve. On iOS Safari
      // the loop would otherwise keep firing on the same QR for ~10fps and
      // the queue-strip layout shift between decodes left the canvas reading
      // stale frames so the *next* (different) QR never registered. We resume
      // (or restart, see safeResume) once the result is processed.
      await readyRef.current
      safePause()
      try {
        const asset = await resolveCode(code)
        if (!asset) {
          setScanError(t("mobile.scan.assetNotFound"))
          setTimeout(() => setScanError(null), 1800)
          return
        }
        if (scannedIdsRef.current.has(asset.id)) return
        setScanned((s) => [asset, ...s])
        setFlash(true)
        setTimeout(() => setFlash(false), 280)
        // Short double-pulse so it's clearly perceptible. Some Android devices
        // ignore vibrations under ~30ms; the gap creates a distinct "tick-tick"
        // that feels like positive feedback rather than a phantom buzz.
        // iOS Safari has no Vibration API, so this is a no-op there — the
        // orange flash overlay above is the cross-platform feedback.
        try {
          navigator.vibrate?.([40, 30, 40])
        } catch {
          // Some browsers throw if the page hasn't received a user gesture.
        }
      } finally {
        // Resume after a beat so the operator has time to pull back from the
        // current QR before the next one is decoded — without this the camera
        // immediately re-reads the same sticker the operator hasn't moved
        // off yet, wastes a cooldown slot, and feels jittery.
        setTimeout(() => {
          inFlightRef.current.delete(code)
          if (pausedRef.current) return
          void safeResume()
        }, 600)
      }
    }
  }, [t])

  // restartScanner — torn-down-and-rebuild path used by the watchdog when a
  // pause/resume cycle leaves the library wedged (state stuck at PAUSED, or
  // iOS Safari has paused the underlying <video> and `resume()` silently
  // failed to bring it back). Returns a promise that resolves once the new
  // scanner is scanning so callers can await readiness.
  const restartScannerRef = useRef<() => Promise<void>>(async () => {})

  // Bring up the camera as soon as the screen mounts. The empty deps array is
  // load-bearing: re-running this effect would tear down a working scanner
  // and try to re-attach to the same DOM node, which leaves the camera in a
  // broken state. The ref above lets us call the freshest decoder without
  // resubscribing.
  //
  // Init is wrapped in a setTimeout so React StrictMode's intentional
  // mount→unmount→mount cycle in dev cancels the first attempt before any
  // getUserMedia call lands. Without this, two scanners briefly fight over
  // the back camera and the survivor often comes up in a half-working state
  // (decodes once and then stops). That's the "sometimes scans, sometimes
  // doesn't" the operator was seeing.
  useEffect(() => {
    let cancelled = false
    let scanner: Html5Qrcode | null = null
    let initTimer: ReturnType<typeof setTimeout> | null = null

    const startScanner = (s: Html5Qrcode) =>
      s
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => onDecodedRef.current(decodedText),
          () => {
            // per-frame parse failure — ignore
          },
        )
        .catch((err) => {
          if (!cancelled) setCameraError(t("mobile.scan.cameraDenied"))
          throw err
        })

    initTimer = setTimeout(() => {
      if (cancelled) return
      scanner = new Html5Qrcode(containerId, /* verbose= */ false)
      scannerRef.current = scanner
      readyRef.current = startScanner(scanner).catch(() => {})

      // Watchdog/recovery helper. Stops the current scanner, throws it away,
      // creates a fresh one. The DOM container is reused so this is cheap
      // enough to do silently on a wedge.
      restartScannerRef.current = async () => {
        const dead = scannerRef.current
        scannerRef.current = null
        if (dead) {
          try {
            await dead.stop()
          } catch {
            // ignore — already stopped or never started
          }
          try {
            dead.clear()
          } catch {
            // ignore
          }
        }
        if (cancelled) return
        const next = new Html5Qrcode(containerId, false)
        scanner = next
        scannerRef.current = next
        readyRef.current = startScanner(next).catch(() => {})
        await readyRef.current
      }
    }, 100)

    return () => {
      cancelled = true
      if (initTimer) clearTimeout(initTimer)
      const dead = scannerRef.current
      scannerRef.current = null
      restartScannerRef.current = async () => {}
      if (dead) {
        readyRef.current
          .catch(() => {})
          .then(() => dead.stop().catch(() => {}))
          .then(() => {
            try {
              dead.clear()
            } catch {
              // ignore
            }
          })
      }
    }
    // Mount-once is intentional — see comment above the effect. The decoder
    // and the camera-denied i18n message are both pulled fresh via refs/closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeAt = (id: MobileAsset["id"]) => {
    setScanned((s) => s.filter((x) => x.id !== id))
    // Drop the expansion too, or re-scanning the same asset brings it back
    // already open.
    setExpandedIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }
  const clearAll = () => {
    setScanned([])
    setExpandedIds(new Set())
  }
  const toggleExpanded = (id: MobileAsset["id"]) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  /**
   * Submit one bulk_stage_move per asset-type group, mirroring the desktop
   * scan flow. Each form passes its own values + the target stage it picked;
   * the controller validates per group and returns errors keyed to that group.
   */
  const handleSubmitMove = async (
    perGroup: Array<{
      type: AssetTypeKey
      values: MoveStageFormValues
      targetStage: StageWithRules
    }>,
  ) => {
    setSubmitting(true)
    setSubmitErrors({})
    const newErrors: Partial<Record<AssetTypeKey, Record<string, string>>> = {}
    const movedStages: StageWithRules[] = []
    let allOk = true
    let movedCount = 0

    for (const { type, values, targetStage } of perGroup) {
      const ids = grouped[type]?.map((a) => a.id) || []
      if (ids.length === 0) continue
      const result = await onConfirm({ asset_type: type, asset_ids: ids, values })
      if (result.ok) {
        movedStages.push(targetStage)
        movedCount += ids.length
      } else {
        newErrors[type] = result.errors
        allOk = false
      }
    }

    setSubmitting(false)

    if (!allOk) {
      setSubmitErrors(newErrors)
      return
    }

    setMoveSheetOpen(false)
    setConfirmation({ count: movedCount, targetStages: movedStages })
    setTimeout(() => {
      setScanned([])
      setConfirmation(null)
    }, 1800)
  }

  return (
    <div
      style={{
        position: "absolute", inset: 0, zIndex: 40,
        background: T.ink, color: "#fff",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}
    >
      {/* Top bar — graphite over the camera with a hairline accent rule */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: "linear-gradient(180deg, rgba(20,24,27,0.85), rgba(20,24,27,0))",
          zIndex: 4,
          position: "relative",
        }}
      >
        <Touchable
          onClick={onClose}
          style={{
            width: 40, height: 40, borderRadius: 0,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.18)",
            display: "grid", placeItems: "center", color: "#fff",
          }}
        >
          <X size={18} color="#fff" />
        </Touchable>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: 10.5,
              color: T.accent,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              fontWeight: 700,
            }}
          >
            {t("mobile.scan.eyebrow")}
          </div>
          <div
            style={{
              fontFamily: T.fontDisplay,
              fontSize: 14,
              fontWeight: 600,
              marginTop: 2,
              letterSpacing: -0.2,
            }}
          >
            {scanned.length === 0
              ? t("mobile.scan.aimAtQr")
              : t("mobile.scan.batchCount", { count: scanned.length })}
          </div>
        </div>
        {/* Spacer keeps the title centered now that the settings button is gone. */}
        <div style={{ width: 40, height: 40 }} aria-hidden />
      </div>

      {/* Viewfinder — clean live camera feed with the library's qrbox
          shading. The library injects <video> (and sometimes <img>/<canvas>)
          directly into the container; we force them to fill the box. */}
      <div
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          background: T.ink,
        }}
      >
        <div
          id={containerId}
          ref={containerRef}
          style={{ position: "absolute", inset: 0, background: "#000" }}
        />
        <style>{`
          #${containerId} > * {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
          }
        `}</style>

        {/* Capture flash — full-screen orange burst when a QR resolves */}
        {flash && (
          <div
            aria-hidden
            style={{
              position: "absolute", inset: 0,
              background: T.accent,
              opacity: 0.35,
              animation: "mFlashBurst 280ms ease-out",
              pointerEvents: "none",
              zIndex: 3,
            }}
          />
        )}

        {/* Status caption — uses the industrial mono treatment */}
        <div
          style={{
            position: "absolute",
            bottom: 24, left: 16, right: 16,
            textAlign: "center",
            fontFamily: T.fontMono,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            zIndex: 3,
            color: "#fff",
            textShadow: "0 1px 2px rgba(0,0,0,0.6)",
            fontWeight: 600,
          }}
        >
          {cameraError ? cameraError : scanError ? scanError : t("mobile.scan.waitingCode")}
        </div>
      </div>

      {/* Scanned queue strip — graphite panel. Capped tight so the camera area
          keeps as much vertical space as possible; the full list is one tap
          away in the review sheet. */}
      {scanned.length > 0 && (
        <div
          style={{
            background: "#1B1F23",
            borderTop: `1px solid ${T.accent}`,
            padding: "8px 12px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 4px 8px",
            }}
          >
            <span
              style={{
                fontFamily: T.fontMono,
                fontSize: 11,
                letterSpacing: 1.2,
                color: T.accent,
                fontWeight: 700,
                textTransform: "uppercase",
              }}
            >
              {t("mobile.scan.batchLabel")} · {scanned.length} {t("mobile.scan.asset", { count: scanned.length })}
            </span>
            <Touchable
              onClick={clearAll}
              style={{
                color: "rgba(255,255,255,0.65)",
                fontSize: 12,
                fontFamily: T.fontMono,
                letterSpacing: 0.8,
                textTransform: "uppercase",
              }}
            >
              {t("mobile.scan.clear")}
            </Touchable>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {scanned.slice(0, 2).map((a, i) => (
              <ScannedRow
                key={a.id}
                asset={a}
                onRemove={() => removeAt(a.id)}
                isNew={i === 0}
              />
            ))}
            {/* Always offered, not just when rows are hidden. The strip shows
                at most two assets, and the review sheet is the only place a
                row can be expanded — gating this on a batch of three left the
                detail of a one- or two-asset scan unreachable. */}
            <Touchable
              onClick={() => setReviewSheetOpen(true)}
              style={{
                padding: "6px 12px",
                textAlign: "center",
                fontFamily: T.fontMono,
                fontSize: 11,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                color: T.accent,
              }}
            >
              {scanned.length > 2
                ? t("mobile.scan.viewMore", { count: scanned.length - 2 })
                : t("mobile.scan.reviewBatch")}
            </Touchable>
          </div>
        </div>
      )}

      {/* Bottom action bar — camera scans continuously, so we only show the
          "Mover" CTA once at least one asset has been captured. */}
      <div
        style={{
          background: T.ink,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          padding: "14px 16px 22px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          minHeight: 92,
        }}
      >
        {scanned.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              color: "rgba(255,255,255,0.55)",
              fontFamily: T.fontMono,
              fontSize: 12,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            <ScanLine size={16} color="rgba(255,255,255,0.55)" />
            {t("mobile.scan.waitingScan")}
          </div>
        ) : (
          <Touchable
            onClick={() => setMoveSheetOpen(true)}
            style={{
              flex: 1,
              height: 56,
              borderRadius: T.radius,
              background: T.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              fontFamily: T.fontDisplay,
              fontSize: 14,
              fontWeight: 700,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: 1,
              border: `2px solid ${T.accent}`,
              boxShadow: `0 4px 14px -4px ${T.accent}cc`,
            }}
          >
            <ArrowLeftRight size={20} color="#fff" strokeWidth={2} />
            {t("mobile.scan.moveButton", { count: scanned.length })}
          </Touchable>
        )}
      </div>

      {/* Bulk move sheet — one form card per scanned asset type. Mirrors the
          desktop scan flow (App::ScanController + groupedAssets). */}
      <BottomSheet
        open={moveSheetOpen}
        onClose={() => (submitting ? undefined : setMoveSheetOpen(false))}
        title={t("mobile.scan.moveSheetTitle", { count: scanned.length })}
      >
        <BulkMoveBody
          groupTypes={groupTypes}
          grouped={grouped}
          stagesByType={stagesByType}
          products={products}
          customers={customers}
          tags={tags}
          errors={submitErrors}
          busy={submitting}
          onSubmit={handleSubmitMove}
        />
      </BottomSheet>

      {/* Review sheet */}
      <BottomSheet
        open={reviewSheetOpen}
        onClose={() => setReviewSheetOpen(false)}
        title={t("mobile.scan.reviewTitle", { count: scanned.length })}
      >
        <div
          style={{
            padding: "8px 16px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {scanned.map((a) => (
            <ScannedRow
              key={a.id}
              asset={a}
              onRemove={() => removeAt(a.id)}
              variant="sheet"
              expanded={expandedIds.has(a.id)}
              onToggle={() => toggleExpanded(a.id)}
            />
          ))}
        </div>
      </BottomSheet>

      {/* Confirmation overlay */}
      {confirmation && (
        <div
          style={{
            position: "absolute", inset: 0, zIndex: 80,
            background: "rgba(20, 24, 27, 0.78)",
            backdropFilter: "blur(6px)",
            display: "grid", placeItems: "center",
            animation: "mfadeIn 200ms",
          }}
        >
          <div
            style={{
              background: T.surface,
              color: T.ink,
              padding: 28,
              borderRadius: T.radius,
              border: `1px solid ${T.border}`,
              borderTop: `3px solid ${T.success}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              maxWidth: 280,
              textAlign: "center",
              animation: "mPopIn 320ms cubic-bezier(.22,1.4,.36,1)",
            }}
          >
            <div
              style={{
                width: 56, height: 56,
                background: T.success, color: "#fff",
                display: "grid", placeItems: "center",
              }}
            >
              <Check size={28} color="#fff" strokeWidth={3} />
            </div>
            <div
              style={{
                fontFamily: T.fontDisplay,
                fontSize: 19,
                fontWeight: 700,
                color: T.ink,
                marginTop: 4,
                letterSpacing: -0.2,
              }}
            >
              {t("mobile.scan.movedSuccess", { count: confirmation.count })}
            </div>
            <div style={{ fontSize: 13.5, color: T.inkSoft, lineHeight: 1.45 }}>
              {t("mobile.scan.stageUpdatedTo")}{" "}
              <strong style={{ color: T.ink }}>
                {confirmation.targetStages.map((s) => s.name).join(" · ")}
              </strong>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface ScannedRowProps {
  asset: MobileAsset
  onRemove: () => void
  isNew?: boolean
  /**
   * `overlay` sits on the dark camera view, `sheet` on the light review
   * bottom sheet. Same row, two palettes — keeping them as one component is
   * what stops the summary line from having to be written twice.
   */
  variant?: "overlay" | "sheet"
  /**
   * Expansion is opt-in per surface. The camera strip stays a fixed two-row
   * glance — growing a row there pushes the "Mover" CTA toward the fold while
   * the operator is still scanning — so only the review sheet passes these.
   */
  expanded?: boolean
  onToggle?: () => void
}

function ScannedRow({
  asset,
  onRemove,
  isNew = false,
  variant = "overlay",
  expanded = false,
  onToggle,
}: ScannedRowProps) {
  const { t } = useTranslation()
  const TypeIcon = assetTypeIcon(asset.assetable_type)
  const overlay = variant === "overlay"
  const segments = assetSummarySegments(asset, t)
  const detailFields = onToggle ? assetDetailFields(asset, t) : []
  const expandable = !!onToggle && (detailFields.length > 0 || asset.tags.length > 0)

  const codeColor = overlay ? "#fff" : T.ink
  const typeLabelColor = overlay ? "rgba(255,255,255,0.6)" : T.inkSoft
  const summaryColor = overlay ? "rgba(255,255,255,0.72)" : T.inkSoft
  const mutedColor = overlay ? "rgba(255,255,255,0.4)" : T.inkMuted
  const closeColor = overlay ? "rgba(255,255,255,0.65)" : T.inkSoft

  return (
    <div
      style={{
        borderRadius: T.radius,
        background: overlay
          ? isNew
            ? "rgba(232, 84, 28, 0.18)"
            : "rgba(255, 255, 255, 0.04)"
          : T.surfaceAlt,
        borderLeft: overlay
          ? `3px solid ${isNew ? T.accent : "rgba(255,255,255,0.18)"}`
          : undefined,
        animation: overlay && isNew ? "mPopIn 240ms cubic-bezier(.22,1.4,.36,1)" : undefined,
        overflow: "hidden",
      }}
    >
      <div
        onClick={expandable ? onToggle : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: overlay ? "8px 12px" : "10px 14px",
          minHeight: 48,
          cursor: expandable ? "pointer" : undefined,
        }}
      >
      {/* Type icon + label tile — replaces the old generic checkmark and tells
          the operator at a glance whether the scanned asset is a keg, fridge,
          cylinder, or beer tap. */}
      <div
        style={{
          width: 48,
          minWidth: 48,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          flexShrink: 0,
        }}
      >
        <TypeIcon size={22} color={T.accent} strokeWidth={2} />
        <span
          style={{
            fontFamily: T.fontMono,
            fontSize: 9,
            letterSpacing: 0.6,
            color: typeLabelColor,
            textTransform: "uppercase",
          }}
        >
          {t(`mobile.assetTypes.${assetTypeI18nKey(asset.assetable_type)}`)}
        </span>
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Code, stage and remove share the first line so the summary below
            gets the column's full width — parked on the right of a single
            shared line it had barely half, and the badge is the widest thing
            competing with it. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: 14,
              fontWeight: 700,
              color: codeColor,
              letterSpacing: 0.2,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {asset.code}
          </div>
          <MStageBadge stage={asset.stage} size="sm" />
          {/* Removing must not also toggle the panel. Touchable's onClick takes
              no event, so the guard sits on a wrapper rather than in the
              shared primitive's signature. */}
          <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", flexShrink: 0 }}>
            <Touchable
              onClick={onRemove}
              style={{
                width: overlay ? 24 : 28,
                height: overlay ? 24 : 28,
                borderRadius: 0,
                display: "grid",
                placeItems: "center",
                color: closeColor,
                border: overlay ? "1px solid rgba(255,255,255,0.18)" : undefined,
                flexShrink: 0,
              }}
            >
              <X size={overlay ? 12 : 14} color={closeColor} />
            </Touchable>
          </span>
        </div>
        {/* Everything the operator needs to confirm the asset carries what it
            should. Stays one line — a wrapped row would push the batch list
            past the fold — so when it overflows only the `shrink` segments
            (free-text names) ellipsize; measurements like "50 L" are short,
            high-value, and stay whole. */}
        {segments.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              minWidth: 0,
              overflow: "hidden",
              fontSize: 11.5,
              lineHeight: 1.3,
              color: summaryColor,
            }}
          >
            {segments.map((s, i) => (
              <span
                key={i}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  flexGrow: 0,
                  flexShrink: s.shrink ?? 0,
                  flexBasis: "auto",
                  minWidth: s.shrink ? 0 : undefined,
                }}
              >
                {i > 0 && (
                  <span style={{ opacity: 0.5, flexShrink: 0, whiteSpace: "pre" }}> · </span>
                )}
                <span
                  style={{
                    color: s.muted ? mutedColor : s.emphasis ? codeColor : undefined,
                    fontWeight: s.emphasis ? 600 : 400,
                    fontStyle: s.muted ? "italic" : undefined,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.text}
                </span>
              </span>
            ))}
            {expandable && (
              <ChevronDown
                size={13}
                color={summaryColor}
                style={{
                  flexShrink: 0,
                  marginLeft: 6,
                  alignSelf: "center",
                  transform: expanded ? "rotate(180deg)" : undefined,
                  transition: "transform 160ms ease",
                }}
              />
            )}
          </div>
        )}
      </div>
      </div>
      {/* Expanded panel — everything the one-line summary had to leave out or
          trim. Sheet-only, so it can wrap freely without costing the operator
          rows above the fold. */}
      {expandable && expanded && (
        <div style={{ padding: "0 14px 10px 14px" }}>
          <div style={{ height: 1, background: T.borderSoft, marginBottom: 2 }} />
          {detailFields.map((f) => (
            <MField key={f.label} label={f.label} value={f.value} mono={f.mono} wrap={f.wrap} />
          ))}
          {asset.tags.length > 0 && (
            <div style={{ paddingTop: 12 }}>
              <div
                style={{
                  fontFamily: T.fontMono,
                  fontSize: 10,
                  color: T.inkSoft,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                {t("mobile.assetDetail.fields.tags")}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {asset.tags.map((tag) => (
                  <MTagChip key={tag.id} tag={tag} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Mobile asset-type icon map. Lucide doesn't ship a "keg" so we reuse the
// Cylinder shape for kegs (matches the inventory list and the desktop UI).
function assetTypeIcon(type: AssetTypeKey): LucideIcon {
  switch (type) {
    case "Keg":
      return Cylinder
    case "Fridge":
      return Refrigerator
    case "Cylinder":
      return Cylinder
    case "BeerTap":
      return Beer
  }
}

// ─── Bulk move body ─────────────────────────────────────────────────────────

interface BulkMoveBodyProps {
  groupTypes: AssetTypeKey[]
  grouped: Partial<Record<AssetTypeKey, MobileAsset[]>>
  stagesByType: StagesByType
  products: Product[]
  customers: Customer[]
  tags: Tag[]
  errors: Partial<Record<AssetTypeKey, Record<string, string>>>
  busy?: boolean
  onSubmit: (
    perGroup: Array<{
      type: AssetTypeKey
      values: MoveStageFormValues
      targetStage: StageWithRules
    }>,
  ) => void
}

interface GroupFormState {
  values: MoveStageFormValues
  targetStage: StageWithRules | null
}

/**
 * Step-through wizard for multi-type bulk moves.
 *
 * Each step shows one type's MoveStageForm with its own stages and rules.
 * "Próximo →" advances client-side only — submission happens once when the
 * user reaches the last step and taps "Confirmar". This keeps the same desktop
 * semantics (one POST per type, all triggered together) while letting the
 * operator focus on one form at a time and not get overwhelmed by stacked
 * cards. If a server-side error comes back for a specific type, the wizard
 * jumps back to that step so the operator sees and fixes the failed group.
 */
function BulkMoveBody({
  groupTypes,
  grouped,
  stagesByType,
  products,
  customers,
  tags,
  errors,
  busy,
  onSubmit,
}: BulkMoveBodyProps) {
  const { t } = useTranslation()

  // Per-group form state, keyed by asset type. Persists across step navigation
  // so going back to a previous step preserves the user's input.
  const [formState, setFormState] = useState<Partial<Record<AssetTypeKey, GroupFormState>>>({})
  const [step, setStep] = useState(0)

  const totalSteps = groupTypes.length
  const isSingleStep = totalSteps <= 1
  const isLastStep = step >= totalSteps - 1
  const currentType = groupTypes[step]

  // Jump to the first step that has server errors so the user can fix it.
  useEffect(() => {
    if (!errors) return
    const failingIndex = groupTypes.findIndex((type) => {
      const e = errors[type]
      return e && Object.keys(e).length > 0
    })
    if (failingIndex >= 0) setStep(failingIndex)
  }, [errors, groupTypes])

  const setGroupValues = (type: AssetTypeKey, values: MoveStageFormValues) => {
    const stage =
      stagesByType[type]?.find((s) => String(s.id) === String(values.stage_id)) || null
    setFormState((prev) => ({ ...prev, [type]: { values, targetStage: stage } }))
  }

  const canAdvance = (type: AssetTypeKey | undefined): boolean => {
    if (!type) return false
    const state = formState[type]
    if (!state || !state.targetStage) return false
    const rules = state.targetStage.rules
    const isKeg = type === "Keg"
    if (isKeg && rules.includes("require_product") && !state.values.product_id) return false
    if (rules.includes("require_customer") && !state.values.customer_id) return false
    if (isKeg && rules.includes("require_batch") && !state.values.batch_number.trim()) {
      return false
    }
    return true
  }

  const handleNext = () => {
    if (!canAdvance(currentType)) return
    setStep((s) => Math.min(s + 1, totalSteps - 1))
  }

  const handleBack = () => setStep((s) => Math.max(s - 1, 0))

  const handleSubmitAll = () => {
    if (!canAdvance(currentType)) return
    const perGroup = groupTypes
      .map((type) => {
        const state = formState[type]
        if (!state?.targetStage) return null
        return { type, values: state.values, targetStage: state.targetStage }
      })
      .filter(
        (g): g is { type: AssetTypeKey; values: MoveStageFormValues; targetStage: StageWithRules } =>
          g !== null,
      )
    if (perGroup.length > 0) onSubmit(perGroup)
  }

  const renderStep = (type: AssetTypeKey) => {
    const assets = grouped[type] || []
    const groupStages = stagesByType[type] || []
    const groupErrors = errors[type] || {}
    return (
      <div key={type}>
        {/* Step header — type name + asset count + step indicator (only when multi-step). */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            padding: "12px 16px 0",
          }}
        >
          <div
            style={{
              fontFamily: T.fontMono,
              fontSize: 11,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              fontWeight: 700,
              color: T.accent,
            }}
          >
            {t(`mobile.assetTypes.${assetTypeI18nKey(type)}`)}
            <span style={{ color: T.inkSoft, marginLeft: 6, fontWeight: 600 }}>
              · {assets.length}
            </span>
          </div>
          {!isSingleStep && (
            <div
              style={{
                fontFamily: T.fontMono,
                fontSize: 10.5,
                letterSpacing: 1,
                textTransform: "uppercase",
                color: T.inkSoft,
                fontWeight: 600,
              }}
            >
              {t("mobile.scan.stepOf", { current: step + 1, total: totalSteps })}
            </div>
          )}
        </div>

        {/* Step progress dots */}
        {!isSingleStep && (
          <div
            aria-hidden
            style={{
              display: "flex",
              gap: 4,
              padding: "8px 16px 0",
            }}
          >
            {groupTypes.map((_, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: 3,
                  background: i <= step ? T.accent : T.borderSoft,
                  borderRadius: 2,
                }}
              />
            ))}
          </div>
        )}

        {/* Asset codes in this step's group — gives the operator a concrete
            list of which assets the form below will move. Wraps; long lists
            get a "+N" overflow chip and the rest are visible in the review sheet. */}
        <StepAssetCodes assets={assets} />

        <GroupMoveForm
          type={type}
          count={assets.length}
          stages={groupStages}
          products={products}
          customers={customers}
          tags={tags}
          errors={groupErrors}
          busy={busy}
          onChange={(values) => setGroupValues(type, values)}
        />
      </div>
    )
  }

  return (
    <div>
      {currentType && renderStep(currentType)}

      <div
        style={{
          padding: "0 16px 24px",
          display: "flex",
          gap: 10,
        }}
      >
        {!isSingleStep && step > 0 && (
          <button
            disabled={busy}
            onClick={handleBack}
            style={{
              flex: 1,
              height: 54,
              borderRadius: T.radius,
              background: T.surface,
              color: T.ink,
              border: `1px solid ${T.border}`,
              fontFamily: T.fontDisplay,
              fontSize: 15,
              fontWeight: 600,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            {t("mobile.scan.back")}
          </button>
        )}
        <button
          disabled={!canAdvance(currentType) || busy}
          onClick={isLastStep ? handleSubmitAll : handleNext}
          style={{
            flex: 2,
            height: 54,
            borderRadius: T.radius,
            background: canAdvance(currentType) ? T.accent : T.surfaceAlt,
            color: canAdvance(currentType) ? "#fff" : T.inkMuted,
            border: `1px solid ${canAdvance(currentType) ? T.accent : T.border}`,
            fontFamily: T.fontDisplay,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            cursor: canAdvance(currentType) ? "pointer" : "not-allowed",
          }}
        >
          {isLastStep
            ? t("mobile.moveStageForm.submit")
            : `${t("mobile.scan.next")} →`}
        </button>
      </div>
    </div>
  )
}

interface GroupMoveFormProps {
  type: AssetTypeKey
  count: number
  stages: StageWithRules[]
  products: Product[]
  customers: Customer[]
  tags: Tag[]
  errors: Record<string, string>
  busy?: boolean
  onChange: (values: MoveStageFormValues) => void
}

/**
 * Wraps MoveStageForm to expose its values via a controlled `onChange` callback.
 * MoveStageForm was originally built around an internal-only submit; for the
 * multi-group flow we need each group's working state lifted up so the parent
 * can submit them all together. We hide the inner submit button by passing a
 * sentinel onSubmit that calls back with the values when invoked, and we
 * intercept changes by re-rendering the form with the latest `initial`.
 *
 * In practice we forward the form's submit (which the parent never lets the
 * user reach because we render our own outer submit). We listen to value
 * changes by giving the form a unique key per type and treating its onSubmit
 * as a pull-from-form event. Simpler approach: render the form with no submit
 * and rely on a small wrapper that fires onChange on every interaction.
 *
 * The path of least surprise here is the dedicated controlled form below.
 */
function GroupMoveForm({
  type,
  count,
  stages,
  products,
  customers,
  tags,
  errors,
  busy,
  onChange,
}: GroupMoveFormProps) {
  const { t } = useTranslation()
  return (
    <MoveStageForm
      assetType={type}
      stages={stages}
      products={products}
      customers={customers}
      tags={tags}
      errors={errors}
      busy={busy}
      intro={t("mobile.scan.applyToAssets", { count })}
      onValuesChange={onChange}
      hideSubmit
    />
  )
}

function assetTypeI18nKey(type: AssetTypeKey): string {
  // Maps Pascal case asset types to the camelCase keys under mobile.assetTypes.
  switch (type) {
    case "Keg":
      return "keg"
    case "Fridge":
      return "fridge"
    case "Cylinder":
      return "cylinder"
    case "BeerTap":
      return "beerTap"
  }
}

/**
 * Wrap-flex of mono code chips for the assets in the current wizard step.
 * Hard-caps at 10 visible codes and renders a "+N" chip for the overflow so
 * a 50-keg batch doesn't push the move form off-screen.
 */
function StepAssetCodes({ assets }: { assets: MobileAsset[] }) {
  const VISIBLE = 10
  if (assets.length === 0) return null
  const visible = assets.slice(0, VISIBLE)
  const overflow = assets.length - visible.length
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        padding: "10px 16px 0",
      }}
    >
      {visible.map((a) => (
        <span
          key={a.id}
          style={{
            fontFamily: T.fontMono,
            fontSize: 11,
            fontWeight: 600,
            color: T.ink,
            background: T.surfaceAlt,
            border: `1px solid ${T.border}`,
            padding: "2px 7px",
            borderRadius: T.radius,
            letterSpacing: 0.2,
            whiteSpace: "nowrap",
          }}
        >
          {a.code}
        </span>
      ))}
      {overflow > 0 && (
        <span
          style={{
            fontFamily: T.fontMono,
            fontSize: 11,
            fontWeight: 700,
            color: T.accent,
            background: T.accentSoft,
            border: `1px solid ${T.accentBorder}`,
            padding: "2px 7px",
            borderRadius: T.radius,
            letterSpacing: 0.2,
            whiteSpace: "nowrap",
          }}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
